import { ProjectLedgerContent } from '@/features/project/projects/detail/ledger/_components/ProjectLedgerContent';
import {
  type ProjectLedgerData,
  useProjectLedgerData,
} from '@/features/project/projects/detail/ledger/_hooks/useProjectLedgerData';
import {
  createProjectLedgerTitle,
  useProjectLedgerPrint,
} from '@/features/project/projects/detail/ledger/_hooks/useProjectLedgerPrintUtils';
import { useMessage } from '@/providers';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateIndividualReportLastOutput } from '../../_hooks/group';
import type { PrintStatus } from './GroupInProjectsTable';

type Props = {
  setPrintStatusByProjectId: Dispatch<SetStateAction<PrintStatus[]>>;
} & Pick<PrintStatus, 'projectId' | 'status'>;

/**
 * データ取得が完了したら、印刷用のDOMを生成し、PDF出力を行う
 */
export const ExportProjectData: FC<Props> = ({
  projectId,
  status,
  setPrintStatusByProjectId,
}) => {
  const ledgerData = useProjectLedgerData(projectId, () => {
    if (ledgerData && !ledgerData.isLoading && ledgerData.hasAllRequiredData) {
      setPrintStatusByProjectId((prev) =>
        prev.map((v) => {
          if (v.projectId !== projectId) {
            return v;
          }
          return {
            ...v,
            // 印刷ステータスに更新
            status: 'printing',
          };
        }),
      );
    }
  });
  if (
    !ledgerData ||
    ledgerData.isLoading ||
    !ledgerData.hasAllRequiredData ||
    status === 'loading'
  ) {
    return null;
  }

  return (
    <ExportProjectDataPrinting
      ledgerData={ledgerData}
      projectId={projectId}
      status={status}
      setPrintStatusByProjectId={setPrintStatusByProjectId}
    />
  );
};

const ExportProjectDataPrinting: FC<
  Props & { ledgerData: ProjectLedgerData }
> = ({ ledgerData, projectId, setPrintStatusByProjectId, status }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { openMessage } = useMessage();
  const { t } = useTranslation();
  const { mutateAsync: updateIndividualReportLastOutputAsync } =
    useUpdateIndividualReportLastOutput();

  const handlePrint = useProjectLedgerPrint({
    contentRef,
    documentTitle: createProjectLedgerTitle(
      ledgerData.project,
      ledgerData.ministryName,
    ),
    onAfterPrint: async () => {
      setPrintStatusByProjectId((prev) =>
        prev.map((v) => {
          if (v.projectId !== projectId) {
            return v;
          }
          return {
            ...v,
            status: 'dataReady',
          };
        }),
      );
      // 先にstatusを更新しないと再レンダリングにより２重でシステム出力ダイアログが立ち上がるので注意
      try {
        await updateIndividualReportLastOutputAsync({ id: projectId });
      } catch {
        openMessage(
          'error',
          t('error-message-failed-to-update-report-last-output'),
        );
      }
    },
  });

  useEffect(() => {
    if (status === 'printing' && contentRef.current) {
      // 少し遅延を入れてDOMが確実に描画されるのを待つ
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [status, handlePrint]);

  return (
    <div ref={contentRef}>
      <ProjectLedgerContent
        key={`${projectId}-print`}
        projectId={projectId}
        ledgerData={ledgerData}
      />
    </div>
  );
};
